import { useEffect, useMemo, useRef, useState } from "react";

import { DayPicker } from "react-day-picker";

import { addMonths, format, parseISO, startOfDay, startOfToday, subMonths } from "date-fns";

import { it } from "date-fns/locale";



interface AuditDatePickerProps {

  value: string;

  onChange: (isoDate: string) => void;

  minDate?: Date;

  className?: string;

}



function toIsoDate(d: Date): string {

  return format(d, "yyyy-MM-dd");

}



const NAV_BTN_CLASS =

  "inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-secondary/10 bg-white text-lg font-bold text-secondary shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary";



export default function AuditDatePicker({

  value,

  onChange,

  minDate: minDateProp,

  className = "",

}: AuditDatePickerProps) {

  const [open, setOpen] = useState(false);

  const wasOpenRef = useRef(false);



  const effectiveMinDate = useMemo(

    () => startOfDay(minDateProp ?? startOfToday()),

    [minDateProp]

  );



  const selected = useMemo(() => {

    if (!value) return undefined;

    try {

      return parseISO(value);

    } catch {

      return undefined;

    }

  }, [value]);



  const [viewMonth, setViewMonth] = useState<Date>(() => selected ?? effectiveMinDate);



  useEffect(() => {

    if (open && !wasOpenRef.current) {

      setViewMonth(selected ?? effectiveMinDate);

    }

    wasOpenRef.current = open;

  }, [open, selected, effectiveMinDate]);



  const displayLabel = selected

    ? format(selected, "EEEE d MMMM yyyy", { locale: it })

    : "Seleziona data";



  const monthLabel = format(viewMonth, "MMMM yyyy", { locale: it });



  return (

    <div className={`relative ${className}`}>

      <button

        type="button"

        onClick={() => setOpen((o) => !o)}

        className="mt-1.5 flex w-full items-center justify-between gap-2 rounded-xl border border-secondary/15 bg-secondary/[0.02] px-3 py-2.5 text-left text-sm outline-none transition hover:border-primary/30 focus:border-primary/40 focus:ring-2 focus:ring-primary/15"

        aria-expanded={open}

        aria-haspopup="dialog"

      >

        <span className={selected ? "font-semibold capitalize text-secondary" : "text-secondary/45"}>

          {displayLabel}

        </span>

        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#cd212a] to-[#a01820] text-sm text-white shadow-sm">

          📅

        </span>

      </button>



      {open && (

        <>

          <button

            type="button"

            className="fixed inset-0 z-[60] cursor-default bg-black/20"

            aria-label="Chiudi calendario"

            onClick={() => setOpen(false)}

          />

          <div

            className="absolute left-0 right-0 z-[70] mt-2 overflow-hidden rounded-2xl border border-secondary/10 bg-white p-3 shadow-2xl shadow-secondary/20"

            onMouseDown={(e) => e.stopPropagation()}

            role="dialog"

            aria-label="Calendario scadenza"

          >

            <div className="mb-3 flex items-center justify-between gap-2">

              <button

                type="button"

                className={NAV_BTN_CLASS}

                aria-label="Mese precedente"

                onClick={() => setViewMonth((m) => subMonths(m, 1))}

              >

                ‹

              </button>

              <span className="pointer-events-none text-sm font-extrabold capitalize text-secondary">

                {monthLabel}

              </span>

              <button

                type="button"

                className={NAV_BTN_CLASS}

                aria-label="Mese successivo"

                onClick={() => setViewMonth((m) => addMonths(m, 1))}

              >

                ›

              </button>

            </div>



            <DayPicker

              mode="single"

              locale={it}

              month={viewMonth}

              onMonthChange={setViewMonth}

              hideNavigation

              selected={selected}

              onSelect={(day) => {

                if (day) {

                  onChange(toIsoDate(day));

                  setOpen(false);

                }

              }}

              disabled={{ before: effectiveMinDate }}

              showOutsideDays

              classNames={{

                root: "audit-day-picker mx-auto w-full",

                months: "flex w-full justify-center",

                month: "w-full space-y-2",

                month_caption: "hidden",

                month_grid: "w-full border-collapse",

                weekdays: "text-[10px] font-bold uppercase text-secondary/40",

                weekday: "h-8 w-9 text-center font-semibold",

                week: "mt-1",

                day: "h-9 w-9 p-0 text-center",

                day_button:

                  "mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-sm font-semibold text-secondary transition hover:bg-indigo-50",

                selected:

                  "[&>button]:!bg-gradient-to-br [&>button]:!from-[#cd212a] [&>button]:!to-[#a01820] [&>button]:!text-white [&>button]:shadow-md",

                today: "[&>button]:ring-2 [&>button]:ring-indigo-200",

                disabled: "opacity-30 pointer-events-none",

                outside: "text-secondary/25 opacity-60",

              }}

            />

            <div className="mt-2 flex justify-between border-t border-secondary/10 pt-2">

              <button

                type="button"

                onClick={() => {

                  onChange(toIsoDate(effectiveMinDate));

                  setViewMonth(effectiveMinDate);

                  setOpen(false);

                }}

                className="text-xs font-bold text-indigo-600 hover:text-indigo-800"

              >

                Oggi

              </button>

              <button

                type="button"

                onClick={() => setOpen(false)}

                className="text-xs font-bold text-secondary/50 hover:text-secondary"

              >

                Chiudi

              </button>

            </div>

          </div>

        </>

      )}

    </div>

  );

}


